import { useEffect, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { LinkItemsApi } from "@/apis/LinkItemsApi";
import { useAuthContextValueHook } from "@/contexts/AuthContext";
import { useUserInfoQuery } from "@/hooks/ReactQuery/UserInfoReactQuery/UserInfoReactQuery";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { CustomBackdropController } from "@/components/backdrop/default/v1";
import { CustomDialog } from "@/components/dialog/v1/CustomDialog";
import { useCreateSurveyMutation, useUpdateSurveyMutation } from "@/hooks/ReactQuery/SurveyReactQuery/SurveyReactQuery";
import {
    isAnswerable,
    MAX_DESCRIPTION_LENGTH,
    MAX_QUESTIONS,
    MAX_RESPONSES,
    MAX_TITLE_LENGTH,
    newId,
    newQuestion,
    QUESTION_TYPES,
    questionError,
    sectionNumbers,
    sectionTargets,
    setSurveyPreviewDraft,
    STATUS_LABEL,
    surveyUrl,
    toQuestionBody,
} from "../surveyQuestions";
import { FgQuestionCard } from "./FgQuestionCard";
import { FgSurveyResults } from "./FgSurveyResults";

const HEADER_LINK = "rounded-lg px-2.5 py-1.5 text-xs font-semibold text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 hover:text-gray-900";
const SWITCH = "relative mt-0.5 h-6 w-10 shrink-0 rounded-full bg-gray-200 transition peer-checked:bg-gray-900 peer-disabled:opacity-40 peer-focus-visible:ring-2 peer-focus-visible:ring-gray-900 peer-focus-visible:ring-offset-2 after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition peer-checked:after:translate-x-4";
const SMALL_FIELD = "rounded-xl bg-gray-50 px-3.5 py-2.5 text-[14px] outline-none ring-1 ring-transparent focus:bg-white focus:ring-2 focus:ring-gray-900";
const TAB = (active) => `relative px-1 pb-3 pt-1 text-[15px] font-bold transition ${active ? 'text-gray-900 after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-gray-900' : 'text-gray-400 hover:text-gray-600'}`;

/**
 * 설문 만들기·고치기 + 응답 보기 (탭). survey 가 없으면 새 설문.
 * onSaved(survey) 는 저장 뒤 호출 (새 설문이면 주소를 설문 ID 로 바꾸는 데 쓴다).
 */
export function FgSurveyEditor({ userPageId, domainName, survey, view, onChangeView, onBack, onSaved }) {
    const createSurveyMutation = useCreateSurveyMutation();
    const updateSurveyMutation = useUpdateSurveyMutation();

    const [title, setTitle] = useState(survey?.title || '');
    const [description, setDescription] = useState(survey?.description || '');
    const [questions, setQuestions] = useState(() => survey?.questions?.length ? survey.questions : [{ ...newQuestion('SINGLE_CHOICE') }]);
    const [collectConsent, setCollectConsent] = useState(survey?.collectConsent || false);
    const [blockRepeat, setBlockRepeat] = useState(survey?.blockRepeat || false);
    const [thanksMessage, setThanksMessage] = useState(survey?.thanksMessage || '');
    const [closeAt, setCloseAt] = useState(survey?.closeAt ?? null);
    const [maxResponses, setMaxResponses] = useState(survey?.maxResponses ?? null);
    const [notifyDaily, setNotifyDaily] = useState(survey?.notifyDaily || false);
    // 문자 알림은 휴대전화 인증(프로필 > 휴대전화)한 운영자만 (제안 알림과 같다)
    const authContextValueHook = useAuthContextValueHook();
    const userInfoQuery = useUserInfoQuery({ isLoggedIn: authContextValueHook?.isLoggedIn, isReady: authContextValueHook?.isReady, accessToken: authContextValueHook?.accessToken });
    const phoneVerified = !!userInfoQuery?.data?.data?.data?.phoneNumber;
    const [activeId, setActiveId] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [dirty, setDirty] = useState(false);

    const status = survey?.status || 'DRAFT';
    const isResults = view === 'RESULTS' && !!survey?.id;
    // 마감 시각·응답 수로 자동 마감된 상태 (서버가 status 를 CLOSED 로 준다)
    const autoClosed = status === 'CLOSED' && ((survey?.closeAt && survey.closeAt <= Date.now()) || (survey?.maxResponses && survey.responseCount >= survey.maxResponses));
    const numbers = sectionNumbers(questions);
    const sectionCount = Object.keys(numbers).length + (questions[0]?.type === 'PAGE' ? 0 : 1);
    // 고급 설정: 자동 마감으로 닫혔으면 펼친 채로 연다
    const [advancedOpen, setAdvancedOpen] = useState(!!autoClosed);
    const advancedSummary = [blockRepeat && '재응답 막기', (closeAt || maxResponses) && '자동 마감', notifyDaily && '문자 알림'].filter(Boolean).join(' · ') || '기본값';

    // 저장하지 않고 창을 닫으려 하면 묻는다
    useEffect(() => {
        if (!dirty) {
            return;
        }
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    // 오른쪽 설문 미리보기 (저장 전 내용 그대로)
    useEffect(() => {
        setSurveyPreviewDraft(isResults ? null : { title, description, questions, collectConsent, domainName });
    }, [title, description, questions, collectConsent, domainName, isResults]);
    useEffect(() => () => setSurveyPreviewDraft(null), []);

    const touch = (setter) => (value) => {
        setter(value);
        setDirty(true);
    }
    const changeQuestions = (updater) => {
        setQuestions(updater);
        setDirty(true);
    }
    const updateQuestion = (next) => changeQuestions(prev => prev.map(q => (q.id === next.id ? next : q)));
    const moveQuestion = (index, to) => changeQuestions(prev => {
        const next = [...prev];
        next.splice(to, 0, next.splice(index, 1)[0]);
        return next;
    });
    /** 선택한 질문 바로 아래(없으면 맨 끝)에 넣는다 */
    const addQuestion = (question) => {
        if (questions.length >= MAX_QUESTIONS) {
            customToast.error(`질문은 ${MAX_QUESTIONS}개까지 넣을 수 있어요.`);
            return;
        }
        const at = questions.findIndex(q => q.id === activeId);
        const index = at === -1 ? questions.length : at + 1;
        changeQuestions(prev => [...prev.slice(0, index), question, ...prev.slice(index)]);
        setActiveId(question.id);
        setTimeout(() => {
            const card = document.querySelector(`[data-survey-question="${question.id}"]`);
            card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            card?.querySelector('input[data-question-title]')?.focus({ preventScroll: true });
        });
    }
    const duplicateQuestion = (question) => addQuestion({
        ...question,
        id: newId(),
        ...(question.options ? { options: question.options.map(o => ({ ...o, id: newId() })) } : {}),
        ...(question.rows ? { rows: question.rows.map(o => ({ ...o, id: newId() })) } : {}),
    });
    const removeQuestion = (question) => {
        if ((question.title || question.options?.some(o => o.label)) && !window.confirm('이 질문을 삭제할까요?')) {
            return;
        }
        changeQuestions(prev => prev.filter(q => q.id !== question.id));
    }

    const save = (nextStatus) => {
        if (!title.trim()) {
            customToast.error('설문 제목을 입력해 주세요.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        const errorIndex = questions.findIndex((q, i) => questionError(q, i));
        if (errorIndex !== -1) {
            customToast.error(questionError(questions[errorIndex], errorIndex));
            setActiveId(questions[errorIndex].id);
            document.querySelector(`[data-survey-question="${questions[errorIndex].id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (nextStatus === 'OPEN' && !questions.some(isAnswerable)) {
            customToast.error('질문을 하나 이상 넣어야 응답을 받을 수 있어요.');
            return;
        }

        const body = { title: title.trim(), description: description.trim(), questions: questions.map(toQuestionBody), status: nextStatus, collectConsent, blockRepeat, thanksMessage: thanksMessage.trim(), closeAt, maxResponses, notifyDaily };
        const options = {
            onSuccess: (saved) => {
                setDirty(false);
                if (nextStatus === 'OPEN' && status !== 'OPEN') {
                    customToast.success('응답을 받기 시작했어요. 링크를 공유하거나 내 페이지에 넣어 보세요.');
                } else if (nextStatus === 'CLOSED' && status !== 'CLOSED') {
                    customToast.success('설문을 마감했어요.');
                } else {
                    customToast.success('저장했어요.');
                }
                onSaved(saved);
            },
        };

        if (survey?.id) {
            updateSurveyMutation.mutate({ id: survey.id, body }, options);
        } else {
            createSurveyMutation.mutate({ body: { userPageId, ...body } }, options);
        }
    }

    const handleBack = () => {
        if (dirty && !window.confirm('저장하지 않은 내용이 있어요. 나갈까요?')) {
            return;
        }
        onBack();
    }

    const url = survey?.id ? surveyUrl(domainName, survey.id) : null;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(url);
            customToast.success('설문 링크를 복사했어요.');
        } catch (error) {
            customToast.error('복사하지 못했어요.');
        }
    }

    // 내 페이지 맨 위에 설문으로 가는 링크 버튼을 넣는다 (일반 링크 모듈이라 페이지 관리에서 옮기거나 지울 수 있다)
    const addToPage = async () => {
        if (!window.confirm(`내 페이지 맨 위에 "${survey.title}" 링크 버튼을 넣을까요?`)) {
            return;
        }
        CustomBackdropController().showBackdrop();
        try {
            await LinkItemsApi().create({
                body: { type: 'LINK', userPageId, title: survey.title, linkUrl: url, description: '', thumbnailUrl: '', linkCategoryId: '', openInApp: false },
            });
            customToast.success('내 페이지에 설문 버튼을 넣었어요. 페이지 관리에서 위치를 바꿀 수 있어요.');
        } catch (error) {
            customToast.error(error?.response?.data?.message || '넣지 못했어요. 다시 시도해 주세요.');
        } finally {
            CustomBackdropController().hideBackdrop();
        }
    }

    return (
        <section className="flex flex-col rounded-[15px] border border-gray-100 bg-white shadow-[var(--defaultBoxShadow)]">
            <div className="flex flex-col gap-3 border-b border-gray-100 px-4 pt-3 min-[993px]:px-6">
                <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={handleBack} className="rounded-lg px-2 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-100">← 설문 목록</button>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${survey?.id ? STATUS_LABEL[status].className : 'bg-gray-100 text-gray-500'}`}>
                        {survey?.id ? STATUS_LABEL[status].label : '새 설문'}
                    </span>
                    {dirty && <span className="text-xs text-gray-400">저장 안 됨</span>}
                    {url && status !== 'DRAFT' &&
                        <div className="ml-auto flex flex-wrap gap-1.5">
                            <button type="button" onClick={copyLink} className={HEADER_LINK}>링크 복사</button>
                            <a href={url} target="_blank" rel="noreferrer" className={HEADER_LINK}>열기 ↗</a>
                            <button type="button" onClick={addToPage} className={HEADER_LINK}>내 페이지에 넣기</button>
                        </div>
                    }
                </div>
                {survey?.id &&
                    <div className="flex gap-5" role="tablist">
                        <button type="button" role="tab" aria-selected={!isResults} onClick={() => onChangeView(null)} className={TAB(!isResults)}>질문</button>
                        <button type="button" role="tab" aria-selected={isResults} onClick={() => onChangeView('RESULTS')} className={TAB(isResults)}>
                            응답 <span className="ml-0.5 rounded-full bg-[var(--mainColor100)] px-1.5 py-0.5 text-[11px] text-[var(--mainColor)]">{survey.responseCount}</span>
                        </button>
                    </div>
                }
            </div>

            {isResults
                ? <FgSurveyResults survey={survey} />
                : <>
                    <div className="flex flex-col gap-3 bg-[#f7f7f9] px-3 py-4 min-[993px]:px-5 min-[993px]:py-5">
                        {autoClosed &&
                            <p className="rounded-xl bg-gray-900 px-4 py-3 text-[13px] text-white break-keep">{survey.closeAt && survey.closeAt <= Date.now() ? '마감 시각이 지나' : '받을 응답 수를 다 채워'} 자동으로 마감됐어요. 다시 받으려면 아래 고급 설정에서 시각이나 응답 수를 바꾸고 "다시 열기"를 눌러 주세요.</p>}
                        {survey?.responseCount > 0 &&
                            <p className="rounded-xl bg-amber-50 px-4 py-3 text-[13px] text-amber-800 break-keep">응답 {survey.responseCount}개가 있는 설문이에요. 질문이나 선택지를 지우면 그 답은 요약·CSV 에서 빠지거나 "(지운 선택지)"로 보여요.</p>}

                        <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-gray-100">
                            <div className="h-1.5 bg-gradient-to-r from-[#ee9ca7] via-[#b794f6] to-[#7aa2ff]" aria-hidden="true" />
                            <div className="flex flex-col gap-2 p-4 min-[993px]:p-5">
                                <input
                                    value={title}
                                    onChange={(e) => touch(setTitle)(e.target.value)}
                                    maxLength={MAX_TITLE_LENGTH}
                                    placeholder="설문 제목"
                                    aria-label="설문 제목"
                                    className="w-full border-0 bg-transparent text-[24px] font-extrabold tracking-tight text-gray-900 outline-none placeholder:text-gray-300"
                                />
                                <textarea
                                    value={description}
                                    onChange={(e) => touch(setDescription)(e.target.value)}
                                    maxLength={MAX_DESCRIPTION_LENGTH}
                                    rows={2}
                                    placeholder="설명 (선택) · 무엇을 묻는지, 언제까지 받는지 알려 주세요"
                                    aria-label="설문 설명"
                                    className="w-full resize-y border-0 bg-transparent text-[14px] leading-relaxed text-gray-600 outline-none placeholder:text-gray-300"
                                />
                            </div>
                        </div>

                        {questions.map((question, index) => (
                            <FgQuestionCard
                                key={question.id}
                                question={question}
                                index={index}
                                count={questions.length}
                                active={activeId === question.id}
                                targets={sectionTargets(questions, index, numbers)}
                                hasSections={sectionCount > 1}
                                sectionNumber={numbers[question.id]}
                                sectionCount={sectionCount}
                                onFocus={() => setActiveId(question.id)}
                                onChange={updateQuestion}
                                onMove={(to) => moveQuestion(index, to)}
                                onDuplicate={() => duplicateQuestion(question)}
                                onRemove={() => removeQuestion(question)}
                            />
                        ))}

                        <button type="button" onClick={() => setAddOpen(true)} className="flex items-center justify-center gap-1.5 rounded-2xl border border-dashed border-gray-300 bg-white/60 py-3.5 text-[14px] font-bold text-gray-600 transition hover:border-gray-400 hover:bg-white hover:text-gray-900">
                            <span className="text-[18px] leading-none" aria-hidden="true">＋</span> 질문 추가
                        </button>

                        <div className="flex flex-col gap-4 rounded-2xl bg-white p-4 ring-1 ring-gray-100 min-[993px]:p-5">
                            <p className="text-[14px] font-bold text-gray-900">설정</p>
                            <label className="flex cursor-pointer items-start gap-3">
                                <span className="min-w-0 flex-1">
                                    <span className="block text-[14px] font-semibold text-gray-800">개인정보 수집·이용 동의 받기</span>
                                    <span className="mt-0.5 block text-[12px] text-gray-500 break-keep">이름·연락처·주소처럼 사람을 알아볼 수 있는 걸 묻는다면 켜 주세요. 응답 끝에 동의 칸이 생기고, 동의해야 보낼 수 있어요.</span>
                                </span>
                                <input type="checkbox" role="switch" checked={collectConsent} onChange={(e) => touch(setCollectConsent)(e.target.checked)} className="peer sr-only" />
                                <span className={SWITCH} aria-hidden="true" />
                            </label>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-[14px] font-semibold text-gray-800">응답 완료 메시지 <span className="font-normal text-gray-400">(선택)</span></span>
                                <textarea value={thanksMessage} onChange={(e) => touch(setThanksMessage)(e.target.value)} maxLength={300} rows={2} placeholder="참여해 주셔서 고마워요." className="w-full resize-y rounded-xl bg-gray-50 px-3.5 py-2.5 text-[14px] outline-none ring-1 ring-transparent focus:bg-white focus:ring-2 focus:ring-gray-900" />
                            </label>
                            <details open={advancedOpen} onToggle={(e) => setAdvancedOpen(e.currentTarget.open)} className="group rounded-xl ring-1 ring-gray-200">
                                <summary className="flex cursor-pointer list-none items-center gap-2 px-3.5 py-3 [&::-webkit-details-marker]:hidden">
                                    <span className="text-[14px] font-semibold text-gray-800">고급 설정</span>
                                    <span className="min-w-0 flex-1 truncate text-[12px] text-gray-400">{advancedSummary}</span>
                                    <span className="text-[12px] text-gray-400 transition group-open:rotate-180" aria-hidden="true">▾</span>
                                </summary>
                                <div className="flex flex-col gap-4 border-t border-gray-100 px-3.5 pb-4 pt-3.5">
                                    <div className="flex flex-col gap-1.5">
                                        <span className="text-[14px] font-semibold text-gray-800">같은 사람의 재응답</span>
                                        <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1" role="radiogroup" aria-label="같은 사람의 재응답">
                                            {[{ value: false, label: '재응답 허용' }, { value: true, label: '재응답 막기' }].map(option => (
                                                <button key={option.label} type="button" role="radio" aria-checked={blockRepeat === option.value} onClick={() => touch(setBlockRepeat)(option.value)} className={`rounded-lg py-2 text-[13px] font-bold transition ${blockRepeat === option.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                        <span className="text-[12px] text-gray-500 break-keep">{blockRepeat ? '한 번 응답한 브라우저는 다시 열면 "이미 응답했어요"가 보여요. 로그인이 없어 다른 기기·시크릿 창까지 막지는 못해요.' : '응답을 보낸 뒤 "다시 응답하기"로 몇 번이든 다시 낼 수 있어요.'}</span>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <span className="text-[14px] font-semibold text-gray-800">자동 마감 <span className="font-normal text-gray-400">(선택, 먼저 닿는 쪽에서 마감)</span></span>
                                        <div className="grid gap-2 min-[600px]:grid-cols-2">
                                            <label className="flex flex-col gap-1">
                                                <span className="text-[12px] text-gray-500">마감 시각</span>
                                                <span className="flex gap-1.5">
                                                    <input type="datetime-local" value={closeAt ? moment(closeAt).format('YYYY-MM-DDTHH:mm') : ''} onChange={(e) => touch(setCloseAt)(e.target.value ? moment(e.target.value).valueOf() : null)} aria-label="마감 시각" className={`${SMALL_FIELD} min-w-0 flex-1`} />
                                                    {closeAt && <button type="button" onClick={() => touch(setCloseAt)(null)} className="shrink-0 rounded-lg px-2 text-[12px] font-semibold text-gray-500 hover:bg-gray-100">지우기</button>}
                                                </span>
                                            </label>
                                            <label className="flex flex-col gap-1">
                                                <span className="text-[12px] text-gray-500">받을 응답 수</span>
                                                <span className="flex items-center gap-1.5">
                                                    <input type="number" min={1} max={MAX_RESPONSES} step={1} value={maxResponses ?? ''} onChange={(e) => touch(setMaxResponses)(e.target.value ? Math.min(MAX_RESPONSES, Math.max(1, Math.floor(Number(e.target.value)))) : null)} placeholder="제한 없음" aria-label="받을 응답 수" className={`${SMALL_FIELD} min-w-0 flex-1`} />
                                                    <span className="shrink-0 text-[13px] text-gray-500">개</span>
                                                </span>
                                            </label>
                                        </div>
                                        <span className="text-[12px] text-gray-500 break-keep">닿으면 방문자에게 마감 안내가 보이고 응답을 더 받지 않아요.</span>
                                    </div>
                                    <label className="flex cursor-pointer items-start gap-3">
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-[14px] font-semibold text-gray-800">새 응답 문자 알림</span>
                                            <span className="mt-0.5 block text-[12px] text-gray-500 break-keep">매일 낮 12시, 지난 알림 뒤로 새 응답이 있으면 하루 한 번 문자로 알려요. 응답 내용은 문자에 넣지 않아요.</span>
                                            {userInfoQuery?.isSuccess && !phoneVerified &&
                                                <span className="mt-1 block text-[12px] text-gray-500">휴대전화 인증을 해야 켤 수 있어요. <Link to="/profile" className="font-semibold text-[var(--mainColor)] underline underline-offset-2">인증하러 가기 →</Link></span>}
                                        </span>
                                        <input type="checkbox" role="switch" checked={notifyDaily} disabled={!notifyDaily && !phoneVerified} onChange={(e) => touch(setNotifyDaily)(e.target.checked)} className="peer sr-only" />
                                        <span className={SWITCH} aria-hidden="true" />
                                    </label>
                                </div>
                            </details>
                        </div>
                    </div>

                    <div className="sticky bottom-0 z-20 flex items-center justify-end gap-2 rounded-b-[15px] border-t border-gray-100 bg-white/95 px-4 py-3 backdrop-blur min-[993px]:px-6">
                        {status === 'DRAFT' &&
                            <>
                                <button type="button" onClick={() => save('DRAFT')} className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">임시저장</button>
                                <button type="button" onClick={() => save('OPEN')} className="rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-gray-700">응답 받기 시작</button>
                            </>
                        }
                        {status === 'OPEN' &&
                            <>
                                <button type="button" onClick={() => save('CLOSED')} className="rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100">마감하기</button>
                                <button type="button" onClick={() => save('OPEN')} className="rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-gray-700">저장</button>
                            </>
                        }
                        {status === 'CLOSED' &&
                            <>
                                <button type="button" onClick={() => save('OPEN')} className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">다시 열기</button>
                                <button type="button" onClick={() => save('CLOSED')} className="rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-gray-700">저장</button>
                            </>
                        }
                    </div>
                </>
            }

            <CustomDialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" backgroundColor="#fff">
                <div className="flex flex-col gap-4 p-5">
                    <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                            <p className="text-[17px] font-extrabold text-gray-900">질문 추가</p>
                            <p className="mt-0.5 text-[12px] text-gray-500">{activeId && questions.some(q => q.id === activeId) ? '선택한 질문 아래에 들어가요' : '맨 아래에 들어가요'}</p>
                        </div>
                        <button type="button" onClick={() => setAddOpen(false)} aria-label="닫기" className="-mr-1 -mt-1 rounded-lg px-2 py-1 text-[20px] leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700">×</button>
                    </div>
                    {[{ title: '질문', types: QUESTION_TYPES.filter(t => isAnswerable({ type: t.value })) }, { title: '구성', types: QUESTION_TYPES.filter(t => !isAnswerable({ type: t.value })) }].map(group => (
                        <div key={group.title} className="flex flex-col gap-2">
                            <p className="text-[12px] font-bold text-gray-500">{group.title}</p>
                            <div className="grid grid-cols-2 gap-1.5 min-[600px]:grid-cols-3">
                                {group.types.map(t => (
                                    <button key={t.value} type="button" onClick={() => { setAddOpen(false); addQuestion(newQuestion(t.value)); }} className="flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 text-left text-[13px] font-semibold text-gray-700 ring-1 ring-gray-200 transition hover:-translate-y-px hover:shadow-sm hover:ring-gray-300">
                                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gray-100 text-[12px] text-gray-500" aria-hidden="true">{t.icon}</span>
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </CustomDialog>
        </section>
    );
}
